import { z } from 'zod';
import type { CustomerCategoryToken } from '@sleed/bizcommerce-api';

/**
 * Customer category edit form. Mirrors `CustomerCategoryToken` and the legacy admin tree-list
 * (Core/client/admin/src/pages/Customers/CustomerCategories/index.js): nm required ≤100, cd required ≤100,
 * dsc optional discount %, iv boolean, si required, pid optional parent. erpcd is carried through unchanged
 * (hidden); the default-language name is saved at the top level via the Accept-Language=el pin.
 */
export const customerCategorySchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	dsc: z.number().nullable(),
	pid: z.number().nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	erpcd: z.string().nullable().optional()
});

export type CustomerCategoryFormValues = z.infer<typeof customerCategorySchema>;

export const EMPTY_CUSTOMER_CATEGORY: CustomerCategoryFormValues = {
	nm: '',
	cd: '',
	dsc: null,
	pid: null,
	iv: true,
	si: 0,
	erpcd: null
};

export function tokenToForm(token: CustomerCategoryToken): CustomerCategoryFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? '',
		dsc: token.dsc ?? null,
		pid: token.pid ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		erpcd: token.erpcd ?? null
	};
}

export function formToToken(form: CustomerCategoryFormValues): CustomerCategoryToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd.trim() || null,
		dsc: form.dsc ?? null,
		pid: form.pid && form.pid > 0 ? form.pid : null,
		iv: form.iv,
		si: form.si,
		erpcd: form.erpcd ?? null
	};
}

export interface CustomerCategoryTreeNode extends CustomerCategoryToken {
	children: CustomerCategoryTreeNode[];
	depth: number;
}

/** All transitive descendant ids of `rootId` (used to keep the parent select acyclic). */
export function collectDescendantIds(flat: CustomerCategoryToken[], rootId: number): Set<number> {
	const childrenOf = new Map<number, number[]>();
	for (const c of flat) {
		if (c.id == null || c.pid == null) continue;
		const arr = childrenOf.get(c.pid) ?? [];
		arr.push(c.id);
		childrenOf.set(c.pid, arr);
	}
	const out = new Set<number>();
	const stack = [rootId];
	while (stack.length) {
		const id = stack.pop()!;
		for (const child of childrenOf.get(id) ?? []) {
			if (!out.has(child)) {
				out.add(child);
				stack.push(child);
			}
		}
	}
	return out;
}

/** Build a parent→child forest from the flat customer-category list, ordered by `si` then `nm`. */
export function buildCustomerCategoryTree(flat: CustomerCategoryToken[]): CustomerCategoryTreeNode[] {
	const byId = new Map<number, CustomerCategoryTreeNode>();
	for (const c of flat) {
		if (c.id != null) byId.set(c.id, { ...c, children: [], depth: 0 });
	}
	const roots: CustomerCategoryTreeNode[] = [];
	for (const node of byId.values()) {
		const parent = node.pid != null && node.pid > 0 ? byId.get(node.pid) : undefined;
		if (parent) parent.children.push(node);
		else roots.push(node);
	}
	const sortRec = (nodes: CustomerCategoryTreeNode[], depth: number) => {
		nodes.sort((a, b) => (a.si ?? 0) - (b.si ?? 0) || (a.nm ?? '').localeCompare(b.nm ?? ''));
		for (const n of nodes) {
			n.depth = depth;
			sortRec(n.children, depth + 1);
		}
	};
	sortRec(roots, 0);
	return roots;
}
